/**
 * Project profitability from ClickUp tracked time.
 *
 *   cost          = trackedHours × hourlyCost
 *   margin        = total project value − cost
 *   marginPercent = margin / total project value
 *   effectiveRate = total project value / trackedHours
 *
 * Pure functions only — no React, so they're easy to reason about and test.
 */

const MS_PER_HOUR = 3_600_000;

/** "102h 29m" — truncated to whole minutes, like the ClickUp list header. */
export function formatTracked(ms: number | null | undefined): string {
  const totalMinutes = Math.max(0, Math.floor((ms ?? 0) / 60_000));
  return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
}

export interface Profitability {
  hours: number;
  cost: number;
  margin: number;
  marginPercent: number | null;
  effectiveRate: number | null;
  /** cost / total project value — drives the budget bar. */
  costRatio: number | null;
}

export function computeProfitability(value: number, trackedMs: number, hourlyCost: number): Profitability {
  const hours = trackedMs / MS_PER_HOUR;
  const cost = hours * hourlyCost;
  const margin = value - cost;
  return {
    hours,
    cost,
    margin,
    marginPercent: value > 0 ? margin / value : null,
    effectiveRate: hours > 0 ? value / hours : null,
    costRatio: value > 0 ? cost / value : null,
  };
}

/** Cost-vs-value bar: green, yellow past 75%, red when over budget. */
export function costBarColor(costRatio: number | null): string {
  if (costRatio == null) return 'bg-gray-300';
  if (costRatio > 1) return 'bg-red-500';
  if (costRatio > 0.75) return 'bg-yellow-400';
  return 'bg-green-500';
}
